import { z } from 'zod';
import { contactSalutationValues } from './contact';

// XT-368 (M26): wire schemas for the eligibility/contact-creation logic
// in apps/crm/api/src/eligibility/eligibility.service.ts, mounted as
// `eligibility.checkEligibility`/`eligibility.createContactFromRegistration`
// -- called remotely by apps/cms-public's server-only, service-account
// tRPC client (the agent login flow lives in the separate CMS database,
// not this repo's own).

export const checkEligibilityInput = z.object({
  email: z.email(),
});

export const checkEligibilityOutput = z.object({
  eligible: z.boolean(),
});

export const createContactFromRegistrationInput = z.object({
  email: z.email(),
  firstName: z.string().min(1).max(256).optional(),
  lastName: z.string().min(1).max(256).optional(),
  country: z.string().min(1).max(100).optional(),
  // The agent's phone from portal settings; null clears it, absent keeps it.
  mobilePhone: z.string().max(50).nullable().optional(),
  // Explicit id: the travel-agent portal mints the uuid when it issues the
  // magic link and only creates the CRM row on redeem, so the main id and the
  // CMS mirror id must be the same value.
  id: z.uuid().optional(),
  // Set when an Advisor accepts an agency invitation.
  companyId: z.uuid().optional(),
});

// The agent's own CRM contact details, edited from the portal's My settings.
// Limits and URL checks match the XT contact form, so a value saved in the
// portal never blocks a consultant's next save of the same contact. A blank
// field clears the column; an absent one leaves it untouched.
const blankToNull = <T extends string>(
  value: T | '' | null | undefined
): T | null | undefined => (value === undefined ? undefined : value || null);

const profileText = (label: string, max: number) =>
  z
    .string()
    .trim()
    .max(max, `${label} must be at most ${max} characters`)
    .nullish()
    .transform(blankToNull);

const profileUrl = (label: string) =>
  z
    .string()
    .trim()
    .max(300, `${label} must be at most 300 characters`)
    .refine(
      (value) =>
        value === '' ||
        z.url({ protocol: /^https?$/ }).safeParse(value).success,
      `${label} must be a full link, e.g. https://…`
    )
    .nullish()
    .transform(blankToNull);

export const agentContactProfileFields = z.object({
  salutation: z
    .enum([...contactSalutationValues, ''], {
      error: 'Choose a salutation from the list',
    })
    .nullish()
    .transform(blankToNull),
  title: profileText('Job title', 100),
  officePhone: profileText('Office phone', 30),
  whatsApp: profileText('WhatsApp', 30),
  preferredLanguage: profileText('Preferred language', 50),
  city: profileText('City', 100),
  streetAddress: profileText('Street address', 200),
  postalCode: profileText('Postal code', 20),
  stateOrRegion: profileText('State / region', 100),
  linkedin: profileUrl('LinkedIn'),
  facebook: profileUrl('Facebook'),
  instagram: profileUrl('Instagram'),
  skypeId: profileText('Skype', 50),
  weChatId: profileText('WeChat', 50),
});

export type AgentContactProfileFields = z.output<
  typeof agentContactProfileFields
>;

export type AgentContactProfileField = keyof AgentContactProfileFields;

export const AGENT_CONTACT_PROFILE_FIELDS = Object.keys(
  agentContactProfileFields.shape
) as AgentContactProfileField[];

export const createContactFromRegistrationOutput = z.object({
  contactId: z.uuid(),
});

// Agency == crm.company. Self-service creation from the travel-agent portal
// takes only a name; the caller is the Owner.
export const createAgencyCompanyInput = z.object({
  name: z.string().min(1).max(200),
  email: z.email().optional(),
  country: z.string().min(1).max(100).optional(),
});

export const createAgencyCompanyOutput = z.object({
  companyId: z.uuid(),
});

export const linkContactToCompanyInput = z.object({
  contactId: z.uuid(),
  companyId: z.uuid(),
});

export type CheckEligibilityInput = z.infer<typeof checkEligibilityInput>;
export type CreateContactFromRegistrationInput = z.infer<
  typeof createContactFromRegistrationInput
>;
export type CreateAgencyCompanyInput = z.infer<typeof createAgencyCompanyInput>;
export type LinkContactToCompanyInput = z.infer<
  typeof linkContactToCompanyInput
>;
